"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActorOptimizeQueryOperationDistinctTermsPushdown = void 0;
const bus_optimize_query_operation_1 = require("@comunica/bus-optimize-query-operation");
const context_entries_1 = require("@comunica/context-entries");
const core_1 = require("@comunica/core");
const utils_algebra_1 = require("@comunica/utils-algebra");
const utils_query_operation_1 = require("@comunica/utils-query-operation");
/**
 * A comunica Distinct Terms Optimize Query Operation Actor.
 *
 * This actor rewrites SELECT DISTINCT queries to use the DistinctTerms operator
 * when querying a single source that supports it.
 */
class ActorOptimizeQueryOperationDistinctTermsPushdown extends bus_optimize_query_operation_1.ActorOptimizeQueryOperation {
    async test(_action) {
        return (0, core_1.passTestVoid)();
    }
    async run(action) {
        const dataFactory = action.context.getSafe(context_entries_1.KeysInitQuery.dataFactory);
        const algebraFactory = new utils_algebra_1.AlgebraFactory(dataFactory);
        // Collect selector shapes of all operations
        const sources = this.getSources(action.operation);
        // eslint-disable-next-line ts/no-unnecessary-type-assertion
        const sourceShapes = new Map(await Promise.all(sources
            .map(async (source) => [
            source,
            await source.source.getSelectorShape(source.context ? action.context.merge(source.context) : action.context),
        ])));
        const operation = utils_algebra_1.algebraUtils.mapOperation(action.operation, {
            [utils_algebra_1.Algebra.Types.DISTINCT]: {
                preVisitor: () => ({ continue: false }),
                transform: (operation) => {
                    // Check if the Project wraps a Distinct Pattern
                    let source;
                    // If we have a JOIN with only one input, rewrite it to bring the sole JOIN child upwards one level.
                    if ((0, utils_algebra_1.isKnownOperation)(operation.input, utils_algebra_1.Algebra.Types.PROJECT) &&
                        (0, utils_algebra_1.isKnownOperation)(operation.input.input, utils_algebra_1.Algebra.Types.JOIN) &&
                        operation.input.input.input.length === 1) {
                        operation.input.input = operation.input.input.input[0];
                    }
                    if (!(0, utils_algebra_1.isKnownOperation)(operation.input, utils_algebra_1.Algebra.Types.PROJECT) ||
                        !(0, utils_algebra_1.isKnownOperation)(operation.input.input, utils_algebra_1.Algebra.Types.PATTERN) ||
                        // eslint-disable-next-line no-cond-assign
                        !(source = (0, utils_query_operation_1.getOperationSource)(operation.input.input))) {
                        return operation;
                    }
                    // Check if the projected variables can be mapped to quad terms
                    const termsMapping = this.mapVariablesToTerms(operation.input.variables, operation.input.input);
                    if (!termsMapping) {
                        return operation;
                    }
                    // Create the DistinctTerms operator (replaces the entire DISTINCT(PROJECT(PATTERN))
                    const distinctTermsOp = algebraFactory.createDistinctTerms(operation.input.variables, termsMapping);
                    // Check if the source supports this operation
                    if (!(0, utils_query_operation_1.doesShapeAcceptOperation)(sourceShapes.get(source), distinctTermsOp)) {
                        return operation;
                    }
                    return (0, utils_query_operation_1.assignOperationSource)(distinctTermsOp, source);
                },
            },
        });
        return { operation, context: action.context };
    }
    /**
     * Collected all sources that are defined within the given operation of children recursively.
     * @param operation An operation.
     */
    getSources(operation) {
        const sources = new Set();
        const sourceAdder = (subOperation) => {
            const src = (0, utils_query_operation_1.getOperationSource)(subOperation);
            if (src) {
                sources.add(src);
            }
            return false;
        };
        utils_algebra_1.algebraUtils.visitOperation(operation, {
            [utils_algebra_1.Algebra.Types.PATTERN]: { visitor: sourceAdder },
        });
        return [...sources];
    }
    /**
     * Map projected variables to quad term positions
     */
    mapVariablesToTerms(variables, pattern) {
        const termsMapping = {};
        const termPositions = [
            { term: pattern.subject, position: 'subject' },
            { term: pattern.predicate, position: 'predicate' },
            { term: pattern.object, position: 'object' },
            { term: pattern.graph, position: 'graph' },
        ];
        // Map each projected variable to its position in the pattern
        for (const variable of variables) {
            let mapped = false;
            for (const { term, position } of termPositions) {
                if (term.termType === 'Variable' && term.equals(variable)) {
                    termsMapping[variable.value] = position;
                    mapped = true;
                    break;
                }
            }
            // If a variable cannot be mapped, we cannot optimize
            if (!mapped) {
                return undefined;
            }
        }
        return termsMapping;
    }
}
exports.ActorOptimizeQueryOperationDistinctTermsPushdown = ActorOptimizeQueryOperationDistinctTermsPushdown;
//# sourceMappingURL=ActorOptimizeQueryOperationDistinctTermsPushdown.js.map